/**
 * Finance Transfers screen (FIN-11) — confirmed transfer history (design §10).
 *
 * A table of confirmed transfers (date, from → to, amount, matchedBy badge)
 * with an unlink action (confirm dialog). "Run matching now" re-runs the
 * deterministic auto-matcher and toasts the result. When suggestions are
 * pending, a banner deep-links into the Review queue.
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Icon } from '@k-os/ui';
import {
  useFinanceTransfers,
  useUnlinkTransfer,
  useRunTransferMatching,
  useFinanceReviewCounts,
  type FinanceTransferDto,
} from '../../api/finance';
import { formatEur, financeErrorMessage } from './finance-format';
import { ConfirmDialog } from '../../components/finance/ConfirmDialog';
import styles from './finance.module.css';

const MATCHED_BY_LABELS: Record<string, string> = {
  rule: 'Auto',
  agent: 'Agent',
  user: 'Manual',
  migration: 'Migrated',
};

export function FinanceTransfersScreen() {
  const transfersQ = useFinanceTransfers('confirmed');
  const countsQ = useFinanceReviewCounts();
  const match = useRunTransferMatching();
  const unlink = useUnlinkTransfer();

  const transfers = transfersQ.data ?? [];
  const suggestedCount = countsQ.data?.suggestedTransfers ?? 0;

  const [unlinking, setUnlinking] = useState<FinanceTransferDto | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const runMatch = () => {
    match.mutate(undefined, {
      onSuccess: (res) => {
        setToast(
          res.confirmed === 0 && res.suggested === 0
            ? 'No new transfers found — everything is already matched.'
            : `Matched ${res.confirmed} new transfer${res.confirmed === 1 ? '' : 's'}` +
                (res.suggested > 0
                  ? ` and flagged ${res.suggested} for review.`
                  : '.'),
        );
        window.setTimeout(() => setToast(null), 6000);
      },
    });
  };

  const confirmUnlink = () => {
    if (!unlinking) return;
    unlink.mutate(unlinking.id, {
      onSuccess: () => setUnlinking(null),
    });
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Transfers</h2>
          <div className={styles.subtitle}>
            Confirmed transfers between your accounts. Transfer legs never count as
            income or expense. Unlink one to return both sides to normal transactions.
          </div>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className="kos-btn"
            disabled={match.isPending}
            onClick={runMatch}
          >
            <Icon name="link" size={12} /> {match.isPending ? 'Matching…' : 'Run matching now'}
          </button>
        </div>
      </header>

      {suggestedCount > 0 && (
        <Link to="/finance/review" className={styles.reviewBanner}>
          <Icon name="review" size={14} />
          <span>
            {suggestedCount} suggested transfer{suggestedCount === 1 ? '' : 's'} awaiting your
            decision in the Review queue.
          </span>
          <Icon name="arrowRight" size={13} />
        </Link>
      )}

      {toast && <div className={styles.toast}>{toast}</div>}
      {match.isError && (
        <div className={styles.fieldError}>{financeErrorMessage(match.error)}</div>
      )}

      {transfersQ.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : transfersQ.isError ? (
        <div className={styles.error}>{financeErrorMessage(transfersQ.error)}</div>
      ) : transfers.length === 0 ? (
        <div className={styles.empty}>
          No confirmed transfers yet. The matcher auto-confirms unambiguous pairs on import;
          ambiguous ones land in the Review queue.
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>From → To</th>
                <th>Matched</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ width: 80 }} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {transfers.map((t) => {
                // External (one-sided) transfers have exactly one leg + a
                // counterparty label; render the untracked side as that label.
                const isExternal = t.counterparty !== null;
                const fromLabel = t.from ? t.from.accountName : (t.counterparty ?? '—');
                const toLabel = t.to ? t.to.accountName : (t.counterparty ?? '—');
                const legDescription = t.from?.description ?? t.to?.description ?? '';
                return (
                <tr key={t.id}>
                  <td className={styles.num} style={{ textAlign: 'left' }}>
                    {t.date}
                  </td>
                  <td>
                    <div className={styles.transferCell}>
                      <span className={styles.owner}>{fromLabel}</span>
                      <Icon name="arrowRight" size={12} />
                      <span className={styles.owner}>{toLabel}</span>
                    </div>
                    <div className={styles.numSub}>{legDescription}</div>
                  </td>
                  <td>
                    <span className={`${styles.badge} ${styles.badgeTransfer}`}>
                      {MATCHED_BY_LABELS[t.matchedBy] ?? t.matchedBy}
                    </span>
                    {isExternal && (
                      <span className={`${styles.badge} ${styles.badgeTransfer}`}>External</span>
                    )}
                  </td>
                  <td className={styles.num} style={{ textAlign: 'right' }}>
                    {formatEur(t.amountEur)}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.iconBtnDanger}
                      onClick={() => setUnlinking(t)}
                      title="Unlink transfer"
                    >
                      Unlink
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!unlinking}
        title="Unlink transfer?"
        message={
          unlinking
            ? unlinking.counterparty !== null
              ? `This will unmark the external transfer to/from "${unlinking.counterparty}". The transaction re-enters income/expense analysis.`
              : `This will separate "${unlinking.from?.accountName ?? '—'} → ${unlinking.to?.accountName ?? '—'}" back into two independent transactions. They'll re-enter income/expense analysis and can be re-matched later.`
            : ''
        }
        confirmLabel={unlink.isPending ? 'Unlinking…' : 'Unlink'}
        confirmDisabled={unlink.isPending}
        destructive
        error={financeErrorMessage(unlink.error)}
        onConfirm={confirmUnlink}
        onClose={() => setUnlinking(null)}
      />
    </div>
  );
}
